// Wraps window.fetch once, globally, so every existing fetch() call in the
// app - and there are dozens, one per page, all raw fetch() with a manually
// attached bearer header - gets silent access-token refresh for free. No
// page needs to change to benefit from this.
//
// On a 401/403 from the API: try one silent refresh via the httpOnly
// refresh cookie, then retry the original request once with the new
// access token. If refresh itself fails (cookie missing/expired), that's
// a real logout - clear storage and send the user to /login instead of
// leaving pages silently blank (the original bug report).
const API_ORIGIN = process.env.REACT_APP_BLADE_API_URL;
const originalFetch = window.fetch.bind(window);

// Concurrent requests expiring together (the common case - several widgets
// fetching on the same page load) must share ONE refresh call, not fire
// one each.
let refreshPromise = null;

const isApiRequest = (url) => typeof url === 'string' && url.startsWith(API_ORIGIN);
const isAuthRoute = (url) => url.includes('/api/users/refresh-token') || url.includes('/api/users/checkuser') || url.includes('/api/users/logout');

const forceLogout = () => {
  localStorage.removeItem('accessToken');
  localStorage.removeItem('userData');
  window.location.href = '/login';
};

// While viewing as a customer, the httpOnly refresh cookie still belongs
// to the real admin (impersonation tokens never touch it - see
// setRefreshCookie in users.controller.js). If a silent refresh ran here
// it would mint a new ACCESS TOKEN FOR THE ADMIN and swap the page back to
// admin identity mid-session with no visible change - worse than just
// logging the view out. Exit impersonation instead: this is exactly the
// "impersonation token expired or hit the read-only guard" case.
const isImpersonating = () => !!localStorage.getItem('adminAccessToken');

const forceExitImpersonation = () => {
  const adminToken = localStorage.getItem('adminAccessToken');
  const adminUserData = localStorage.getItem('adminUserData');
  localStorage.removeItem('adminAccessToken');
  localStorage.removeItem('adminUserData');
  localStorage.removeItem('impersonationTargetId');
  if (adminToken) localStorage.setItem('accessToken', adminToken);
  if (adminUserData) localStorage.setItem('userData', adminUserData);
  window.location.href = '/users';
};

const refreshAccessToken = () => {
  if (!refreshPromise) {
    refreshPromise = originalFetch(`${API_ORIGIN}/api/users/refresh-token`, {
      method: 'POST',
      credentials: 'include'
    })
      .then(async (res) => {
        if (!res.ok) return null;
        const data = await res.json();
        localStorage.setItem('accessToken', data.token);
        return data.token;
      })
      .catch(() => null)
      .finally(() => {
        refreshPromise = null;
      });
  }
  return refreshPromise;
};

window.fetch = async (input, init = {}) => {
  const url = typeof input === 'string' ? input : input.url;
  const response = await originalFetch(input, init);

  if (!isApiRequest(url) || isAuthRoute(url) || (response.status !== 401 && response.status !== 403)) {
    return response;
  }

  if (isImpersonating()) {
    forceExitImpersonation();
    return response; // navigation is already underway
  }

  const newToken = await refreshAccessToken();
  if (!newToken) {
    forceLogout();
    return response; // navigation is already underway
  }

  const retryInit = {
    ...init,
    headers: {
      ...(init.headers || {}),
      authorization: 'bearer ' + newToken
    }
  };
  return originalFetch(input, retryInit);
};
